import React from 'react';
import { View, Text } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import AdminButton from '../../../Admin/components/AdminButton';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import { DropdownSelect } from './DropdownSelect';
import { NumberStepperInput } from './NumberStepperInput';
import { LocationOnboardingConfig } from '../types';
import { formatPublishedSummary } from '../utils';
import { styles } from '../PeopleHubConfiguration.styles';

/**
 * `appliesToValue` is `'national'` for the national ramp, or a location name for an override.
 * `hasOverride` — true when the selected location already has its own saved override.
 */
interface LocationAndTimelineCardProps {
  org: string;
  dept: string;
  appliesToValue: string;
  locationsList: string[];
  hasOverride: boolean;
  onAppliesToChange: (val: string) => void;
  onRemoveOverride: () => void;
  onboardingWeeks: number;
  onTimelineChange: (weeks: number) => void;
  publishedConfig: LocationOnboardingConfig;
  /** True when publishedConfig comes from the national ramp (location selected but no override). */
  isInherited: boolean;
}

export const LocationAndTimelineCard: React.FC<LocationAndTimelineCardProps> = ({
  org,
  dept,
  appliesToValue,
  locationsList,
  hasOverride,
  onAppliesToChange,
  onRemoveOverride,
  onboardingWeeks,
  onTimelineChange,
  publishedConfig,
  isInherited,
}) => {
  const isShortening = onboardingWeeks < publishedConfig.onboardingWeeks;
  const isNational = appliesToValue === 'national';

  // Badge: National → "National" (blue)
  //        Location with own override → "Location override" (blue) + Remove Override btn
  //        Location inheriting national → "Uses national" (grey)
  const badge = isNational ? 'National' : hasOverride ? 'Location override' : 'Uses national';
  const badgeStyle = !isNational && !hasOverride ? badgeStyles.usesNational : badgeStyles.locationGoverned;
  const badgeTextStyle = !isNational && !hasOverride ? badgeStyles.usesNationalText : badgeStyles.locationGovernedText;

  const options = [
    { value: 'national', label: `National (every location)` },
    ...locationsList.map((loc) => ({
      value: loc,
      label: hasOverride && appliesToValue === loc ? `${loc} (override)` : loc,
    })),
  ];

  // Published summary sub-text
  let summarySubText: string;
  if (isInherited) {
    summarySubText = `Using the ${org} ${dept} national ramp`;
  } else if (publishedConfig.updatedOn && publishedConfig.updatedBy) {
    summarySubText = `Last updated by ${publishedConfig.updatedBy} on ${new Date(
      publishedConfig.updatedOn
    ).toLocaleDateString('en-US')}`;
  } else {
    summarySubText = 'Using the standard People Hub glidepath';
  }

  const publishedSummary = formatPublishedSummary(publishedConfig);

  // Warning text differs for national vs location
  const warningTail = isNational ? 'from the national glidepath.' : "from this location's glidepath.";

  return (
    <View style={[styles.card, styles.cardPad]}>
      {/* Header */}
      <View style={styles.cardHeader}>
        <View style={styles.cardTitleGroup}>
          <Text style={styles.cardTitle}>Location and Timeline</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <View style={badgeStyle}>
            <Text style={badgeTextStyle}>{badge}</Text>
          </View>
          {!isNational && hasOverride && (
            <AdminButton
              label="Remove Override"
              variant="secondary"
              small
              onPress={onRemoveOverride}
            />
          )}
        </View>
      </View>

      {/* Settings Grid */}
      <View style={styles.locationSettingsGrid}>
        {/* Applies-to Dropdown (national or location) */}
        <View style={styles.locationFieldCol}>
          <Text style={styles.formLabel}>Applies to</Text>
          <DropdownSelect
            value={appliesToValue}
            options={options}
            onChange={onAppliesToChange}
            placeholder="Select…"
          />
          <Text style={styles.formHint}>National covers every location without an override.</Text>
        </View>

        {/* Onboarding Timeline Stepper */}
        <View style={styles.timelineFieldCol}>
          <Text style={styles.formLabel}>Onboarding timeline</Text>
          <View style={styles.inputWithSuffixRow}>
            <NumberStepperInput
              value={onboardingWeeks}
              onChange={onTimelineChange}
              min={4}
              max={26}
              step={1}
            />
            <Text style={styles.suffixText}>weeks</Text>
          </View>
          <Text style={styles.formHint}>Minimum 4 weeks; maximum 26 weeks.</Text>
        </View>

        {/* Published Configuration Sub-Card */}
        <View style={styles.publishedConfigSubCard}>
          <Text style={styles.publishedConfigLabel}>PUBLISHED CONFIGURATION</Text>
          <Text style={styles.publishedConfigValue}>{publishedSummary}</Text>
          <Text style={styles.publishedConfigSubtext}>{summarySubText}</Text>
        </View>
      </View>

      {/* Warning Box when timeline is shortened */}
      {isShortening && (
        <View style={styles.warningBox}>
          <MaterialIcons name="warning" size={18} color="#814705" />
          <Text style={styles.warningText}>
            Saving will remove target weeks {onboardingWeeks + 1} through{' '}
            {publishedConfig.onboardingWeeks} {warningTail}
          </Text>
        </View>
      )}
    </View>
  );
};

// Local badge styles for the two new badge variants not in the shared stylesheet
import { StyleSheet } from 'react-native';
const badgeStyles = StyleSheet.create({
  locationGoverned: {
    backgroundColor: AdminColors.blue75,
    height: 24,
    paddingHorizontal: 8,
    borderRadius: 4,
    justifyContent: 'center',
  },
  locationGovernedText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.blue900,
  },
  usesNational: {
    backgroundColor: AdminColors.gray25,
    height: 24,
    paddingHorizontal: 8,
    borderRadius: 4,
    justifyContent: 'center',
  },
  usesNationalText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.textMuted,
  },
});
